import { useState, useRef, useEffect } from "react";
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart
} from "recharts";
import {
  Home, ShoppingCart, Users, Printer, Search,
  DollarSign, Hash, Zap, Star, TrendingUp, Clock,
  CheckCircle, BarChart3, FileText, Bell, ChevronDown,
  Filter, Settings, Calendar, ChevronLeft, ChevronRight,
  X, Package, Truck, AlertCircle, XCircle, SlidersHorizontal,
  RefreshCcw, Menu, Shirt, Coffee, Layers, TrendingDown, CreditCard, IndianRupee
} from "lucide-react";
import { useDashboard } from "./context/DashboardContext"
import { C, CHART_COLORS } from "../theme";

// Local aliases
const BARS = CHART_COLORS;

// Gradient definitions for charts
const GRADIENT_DEFS = (
  <defs>
    <linearGradient id="gTeal" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#0F766E" stopOpacity={1} />
      <stop offset="100%" stopColor="#14B8A6" stopOpacity={0.8} />
    </linearGradient>
    <linearGradient id="gTealArea" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#0F766E" stopOpacity={0.18} />
      <stop offset="100%" stopColor="#0F766E" stopOpacity={0} />
    </linearGradient>
    <linearGradient id="gAccent" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#F59E0B" stopOpacity={1} />
      <stop offset="100%" stopColor="#FCD34D" stopOpacity={0.8} />
    </linearGradient>
    <linearGradient id="gPurple" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#7C3AED" stopOpacity={1} />
      <stop offset="100%" stopColor="#A78BFA" stopOpacity={0.8} />
    </linearGradient>
    <linearGradient id="gGreen" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#10B981" stopOpacity={1} />
      <stop offset="100%" stopColor="#34D399" stopOpacity={0.8} />
    </linearGradient>
    <linearGradient id="gRed" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#EF4444" stopOpacity={1} />
      <stop offset="100%" stopColor="#FCA5A5" stopOpacity={0.8} />
    </linearGradient>
    <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#3B82F6" stopOpacity={1} />
      <stop offset="100%" stopColor="#93C5FD" stopOpacity={0.8} />
    </linearGradient>
  </defs>
);
const GRADIENT_FILLS = ["url(#gTeal)","url(#gTealArea)","url(#gAccent)","url(#gPurple)","url(#gBlue)","url(#gGreen)"];
const BAR_GRADIENTS  = ["url(#gTeal)","url(#gBlue)","url(#gAccent)","url(#gPurple)","url(#gGreen)","url(#gRed)"];

const TODAY = new Date();
const CURRENT_YEAR  = TODAY.getFullYear();
const CURRENT_MONTH = TODAY.getMonth() + 1;
const CURRENT_DAY   = TODAY.getDate();

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_OF_WEEK = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function isFutureDate(year, month, day) {
  const d = new Date(year, month - 1, day);
  const todayMidnight = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
  return d > todayMidnight;
}

// Build per-day stats from real rawOrders for a given year+month.
// Falls back to zeros for days with no orders.
function buildDayDataFromOrders(rawOrders, year, month) {
  const days = new Date(year, month, 0).getDate();
  // Initialise every day with zeros
  const result = {};
  for (let d = 1; d <= days; d++) {
    result[d] = { placed:0, delivered:0, pending:0, cancelled:0, revenue:0 };
  }

  const STATUS_PLACED    = ["placed"];
  const STATUS_DELIVERED = ["delivered"];
  const STATUS_PENDING   = ["inspection_pending", "pending"];
  const STATUS_CANCELLED = ["cancelled", "return_requested", "refunded"];

  (rawOrders ?? []).forEach(order => {
    if (!order.date) return;
    const datePart = order.date.slice(0, 10); // "YYYY-MM-DD"
    const [y, m, d] = datePart.split("-").map(Number);
    if (y !== year || m !== month) return;
    if (d < 1 || d > days) return;

    const s = (order.status ?? "").toLowerCase();
    const rev = parseFloat(order.amount ?? 0);

    if (STATUS_PLACED.includes(s))    { result[d].placed    += 1; result[d].revenue += rev; }
    else if (STATUS_DELIVERED.includes(s)) { result[d].delivered += 1; result[d].revenue += rev; }
    else if (STATUS_PENDING.includes(s))   { result[d].pending   += 1; }
    else if (STATUS_CANCELLED.includes(s)) { result[d].cancelled += 1; }
    else { result[d].placed += 1; result[d].revenue += rev; } // default: treat unknown as placed
  });

  return result;
}

// Legacy fallback (used when rawOrders not yet loaded)
function generateDayData(year, month) {
  const days = new Date(year, month, 0).getDate();
  const result = {};
  for (let d = 1; d <= days; d++) {
    result[d] = { placed:0, delivered:0, pending:0, cancelled:0, revenue:0 };
  }
  return result;
}

const fmt = (n) => "₹" + Number(n ?? 0).toLocaleString("en-IN");

function todayString() {
  const y = TODAY.getFullYear();
  const m = String(TODAY.getMonth() + 1).padStart(2, "0");
  const d = String(TODAY.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DEFAULT_FILTER = {
  year:     CURRENT_YEAR,
  month:    CURRENT_MONTH,
  day:      CURRENT_DAY,
  fromDate: todayString(),
  toDate:   todayString(),
};

function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    return w < 480 ? "xs" : w < 768 ? "sm" : w < 1024 ? "md" : "lg";
  });
  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setBp(w < 480 ? "xs" : w < 768 ? "sm" : w < 1024 ? "md" : "lg");
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return bp;
}

function KpiCard({ label, value, icon: Icon, tint, sub, trend }) {
  const map = {
    default: { bg:"#F0FDFA", iconBg:"#CCFBF1", clr:C.teal,    border:"#99F6E4" },
    accent:  { bg:"#FFFBEB", iconBg:"#FEF3C7", clr:C.accent,  border:"#FDE68A" },
    purple:  { bg:"#F5F3FF", iconBg:"#EDE9FE", clr:C.purple,  border:"#C4B5FD" },
    red:     { bg:"#FFF1F2", iconBg:"#FFE4E6", clr:C.red,     border:"#FECDD3" },
    green:   { bg:"#F0FDF4", iconBg:"#DCFCE7", clr:C.green,   border:"#BBF7D0" },
  };
  const cfg = map[tint || "default"];
  return (
    <div style={{
      background: C.white, border:`1px solid ${C.border}`, borderRadius:16,
      padding:"18px 16px", display:"flex", flexDirection:"column", gap:14,
      boxShadow: C.shadow, transition:"all .2s", cursor:"default",
    }}
    onMouseEnter={e=>{ e.currentTarget.style.boxShadow=C.shadowHover; e.currentTarget.style.transform="translateY(-2px)"; }}
    onMouseLeave={e=>{ e.currentTarget.style.boxShadow=C.shadow; e.currentTarget.style.transform="translateY(0)"; }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div style={{ width:42, height:42, borderRadius:12, background:cfg.iconBg,
          border:`1.5px solid ${cfg.border}`, display:"flex", alignItems:"center",
          justifyContent:"center", flexShrink:0 }}>
          <Icon size={19} color={cfg.clr} />
        </div>
        {trend != null && (
          <span style={{
            display:"flex", alignItems:"center", gap:2,
            background: trend >= 0 ? "#DCFCE7" : "#FEE2E2",
            color: trend >= 0 ? "#15803D" : "#DC2626",
            borderRadius:20, padding:"3px 8px", fontSize:10.5, fontWeight:700,
          }}>{trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%</span>
        )}
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, letterSpacing:"-0.03em",
          lineHeight:1.1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{value}</div>
        <div style={{ fontSize:10.5, fontWeight:600, color:C.textSoft, textTransform:"uppercase",
          letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
        {sub && <div style={{ fontSize:10.5, color:cfg.clr, marginTop:3, fontWeight:700 }}>{sub}</div>}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, style, headerRight }) {
  return (
    <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16,
      boxShadow:C.shadow, overflow:"hidden", ...style }}>
      {title && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"14px 18px 12px", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {Icon && (
              <div style={{ width:28, height:28, borderRadius:8, background:"#F0FDFA",
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Icon size={13} color={C.teal} />
              </div>
            )}
            <span style={{ fontSize:13, fontWeight:700, color:C.text, letterSpacing:"-0.01em" }}>{title}</span>
          </div>
          {headerRight}
        </div>
      )}
      <div style={{ padding:"16px 16px 14px" }}>{children}</div>
    </div>
  );
}

// Shared modern chart tooltip
function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:"rgba(255,255,255,0.97)", backdropFilter:"blur(16px)",
      border:`1px solid ${C.border}`, borderRadius:12,
      padding:"10px 14px", boxShadow:"0 8px 32px rgba(0,0,0,0.14)",
      fontSize:12, minWidth:140,
    }}>
      {label && <div style={{ fontWeight:700, color:C.textSoft, fontSize:10.5,
        textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:8,
          marginBottom: i < payload.length - 1 ? 5 : 0 }}>
          <span style={{ width:8, height:8, borderRadius:"50%",
            background: p.color || p.fill || C.teal, flexShrink:0 }} />
          <span style={{ color:C.textMid, fontWeight:500, flex:1 }}>{p.name}</span>
          <span style={{ fontWeight:800, color:C.text, paddingLeft:8 }}>
            {formatter ? formatter(p.value, p.name) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// Shared axis + grid props
const AXIS_STYLE = { fontSize:10.5, fill:C.textSoft, fontWeight:500 };
const GRID_PROPS = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };

function FilterDropdown({ label, value, options, onChange, width=130 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const displayLabel = options.find(o => String(o.value) === String(value))?.label || value;
  const isActive = String(value) !== "all";
  return (
    <div ref={ref} style={{ position:"relative", width, flexShrink:0 }}>
      <button onClick={() => setOpen(!open)} style={{
        width:"100%", padding:"7px 10px", borderRadius:10,
        border:`1.5px solid ${isActive ? C.teal : C.border}`,
        background: isActive ? `rgba(15,118,110,0.06)` : C.white,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        cursor:"pointer", fontSize:12, fontWeight:600,
        color: isActive ? C.teal : C.textMid,
        transition:"all .18s",
        boxShadow: open ? `0 0 0 3px rgba(15,118,110,0.12)` : "none",
      }}>
        <span style={{ display:"flex", alignItems:"center", gap:4, overflow:"hidden" }}>
          <span style={{ fontSize:9.5, fontWeight:500, color:C.textSoft, flexShrink:0 }}>{label}:</span>
          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayLabel}</span>
        </span>
        <ChevronDown size={12} color={isActive ? C.teal : C.textSoft}
          style={{ transform:open?"rotate(180deg)":"none", transition:"transform .2s", flexShrink:0, marginLeft:4 }} />
      </button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 5px)", left:0, width:"100%", minWidth:130,
          background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12,
          overflow:"auto", maxHeight:220, zIndex:300,
          boxShadow:"0 8px 32px rgba(15,118,110,0.14)",
        }}>
          {options.map(opt => (
            <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding:"8px 12px", cursor:"pointer",
                background: String(value) === String(opt.value) ? `rgba(15,118,110,0.08)` : "transparent",
                fontSize:12, fontWeight: String(value) === String(opt.value) ? 700 : 500,
                color: String(value) === String(opt.value) ? C.teal : C.text,
                borderBottom:`1px solid ${C.border}`,
              }}>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarPopup({ filter, onSelectDate, onClose, realDayData, realYear, realMonth }) {
  const [viewYear,  setViewYear]  = useState(filter.year  !== "all" ? Number(filter.year)  : CURRENT_YEAR);
  const [viewMonth, setViewMonth] = useState((filter.month !== "all" ? Number(filter.month) : CURRENT_MONTH) - 1);
  const [hoverDay,  setHoverDay]  = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";

  // Use real API data for the loaded month; empty zeros for other months
  const isRealMonth = realDayData && viewYear === realYear && (viewMonth + 1) === realMonth;
  const dayData = isRealMonth ? realDayData : generateDayData(viewYear, viewMonth + 1);
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const DOT_TYPES = [
    { key:"placed",    color:C.teal,    label:"Placed" },
    { key:"delivered", color:"#22c55e", label:"Delivered" },
    { key:"pending",   color:C.accent,  label:"Pending" },
    { key:"cancelled", color:"#ef4444", label:"Cancelled" },
  ];

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      display:"flex", alignItems:isMobile?"flex-end":"flex-start",
      justifyContent:isMobile?"center":"flex-end",
      paddingTop:isMobile?0:80, paddingRight:isMobile?0:28,
      background: isMobile ? "rgba(0,0,0,0.4)" : "transparent",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:C.white, borderRadius:isMobile?"20px 20px 0 0":20,
        width:isMobile?"100%":420, maxWidth:"100%",
        boxShadow:"0 20px 60px rgba(15,118,110,0.18)",
        border:`1.5px solid ${C.border}`,
        overflow:"hidden",
        maxHeight:isMobile?"90vh":"auto",
        overflowY:"auto",
      }}>
        <div style={{ background:`linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`,
          padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={prevMonth} style={{ background:"rgba(255,255,255,0.15)", border:"none",
              borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer", color:C.white }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize:14, fontWeight:700, color:C.white, minWidth:150, textAlign:"center" }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} style={{ background:"rgba(255,255,255,0.15)", border:"none",
              borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer", color:C.white }}>
              <ChevronRight size={14} />
            </button>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none",
            borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer", color:C.white }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, padding:"10px 12px 4px",
          background:`rgba(15,118,110,0.04)` }}>
          {DAYS_OF_WEEK.map(d => (
            <div key={d} style={{ textAlign:"center", fontSize:9.5, fontWeight:700,
              color:C.textSoft, padding:"4px 0" }}>{d}</div>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, padding:"4px 12px 12px" }}>
          {Array.from({ length: firstDay }, (_, i) => <div key={`blank-${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const data = dayData[day];
            const isHover    = hoverDay === day;
            const isSelected = selectedDay === day;
            const isFuture   = isFutureDate(viewYear, viewMonth + 1, day);

            return (
              <div key={day}
                onClick={() => {
                  if (!isFuture) {
                    setSelectedDay(day);
                    onSelectDate && onSelectDate(viewYear, viewMonth + 1, day);
                  }
                }}
                onMouseEnter={() => !isFuture && setHoverDay(day)}
                onMouseLeave={() => setHoverDay(null)}
                style={{
                  borderRadius:8, padding:"5px 2px", cursor: isFuture ? "default" : "pointer",
                  minHeight:46,
                  background: isFuture
                    ? "transparent"
                    : isSelected ? `rgba(15,118,110,0.12)` : isHover ? `rgba(15,118,110,0.05)` : "transparent",
                  border: isSelected && !isFuture ? `1.5px solid ${C.teal}` : "1.5px solid transparent",
                  transition:"all .14s",
                  opacity: isFuture ? 0.35 : 1,
                }}>
                <div style={{ textAlign:"center", fontSize:11, fontWeight: isSelected && !isFuture ? 800 : 600,
                  color: isFuture ? C.textSoft : isSelected ? C.teal : C.text, marginBottom:2 }}>{day}</div>
                {!isFuture && (
                  <div style={{ display:"flex", justifyContent:"center", gap:2, flexWrap:"wrap" }}>
                    {DOT_TYPES.filter(dt => data[dt.key] > 0).map(dt => (
                      <div key={dt.key} style={{ width:4, height:4, borderRadius:"50%", background:dt.color }} />
                    ))}
                  </div>
                )}
                {!isFuture && isHover && (
                  <div style={{ fontSize:7.5, fontWeight:700, color:C.teal, textAlign:"center",
                    background:`rgba(15,118,110,0.08)`, borderRadius:3, padding:"1px 2px", marginTop:1 }}>
                    {fmt(data.revenue)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding:"8px 16px 12px", borderTop:`1px solid ${C.border}`,
          display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
          {DOT_TYPES.map(dt => (
            <div key={dt.key} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:dt.color }} />
              <span style={{ fontSize:10, fontWeight:600, color:C.textMid }}>{dt.label}</span>
            </div>
          ))}
        </div>
        {selectedDay && !isFutureDate(viewYear, viewMonth + 1, selectedDay) && (
          <div style={{ margin:"0 12px 12px", borderRadius:12, background:`rgba(15,118,110,0.05)`,
            border:`1px solid ${C.border}`, padding:"10px 12px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.teal, marginBottom:8 }}>
              {MONTHS[viewMonth]} {selectedDay}, {viewYear}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5 }}>
              {[
                { label:"Placed",    val:dayData[selectedDay].placed,    color:C.teal },
                { label:"Delivered", val:dayData[selectedDay].delivered, color:"#22c55e" },
                { label:"Pending",   val:dayData[selectedDay].pending,   color:C.accent },
                { label:"Cancelled", val:dayData[selectedDay].cancelled, color:"#ef4444" },
              ].map(item => (
                <div key={item.label} style={{ textAlign:"center", padding:"7px 4px",
                  background:C.white, borderRadius:8, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:14, fontWeight:800, color:item.color }}>{item.val}</div>
                  <div style={{ fontSize:9, color:C.textSoft, fontWeight:600, marginTop:1 }}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:7, padding:"7px 10px", background:C.white,
              borderRadius:8, border:`1px solid ${C.border}`, display:"flex",
              alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:11, fontWeight:600, color:C.textMid }}>Daily Revenue</span>
              <span style={{ fontSize:13, fontWeight:800, color:C.teal }}>
                {fmt(dayData[selectedDay].revenue)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FilterBar — now reads/writes context instead of local filter state ────────
function FilterBar({ filter, setFilter, activeCount, onReset }) {
  const { setSelectedYear, setSelectedMonth, derived, selectedYear, selectedMonth } = useDashboard();
  // Build real day data from API orders for the currently loaded month
  const realDayData = derived
    ? buildDayDataFromOrders(derived.rawOrders, selectedYear, selectedMonth)
    : null;
  const [calOpen, setCalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";

  const yearOptions = [
    { value: "all", label: "All Years" },
    { value: 2023,  label: "2023" },
    { value: 2024,  label: "2024" },
    { value: 2025,  label: "2025" },
    { value: 2026,  label: "2026" },
  ];
  const monthOptions = [
    { value:"all", label:"All Months" },
    ...MONTHS.map((m, i) => ({ value: i + 1, label: m })),
  ];

  const handleCalDate = (y, m, d) => {
    setFilter(f => ({ ...f, year: y, month: m, day: d }));
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  // Sync year/month changes to context
  const handleYearChange = (v) => {
    setFilter(f => ({ ...f, year: v, fromDate:"", toDate:"" }));
    if (v !== "all") setSelectedYear(Number(v));
  };
  const handleMonthChange = (v) => {
    setFilter(f => ({ ...f, month: v, fromDate:"", toDate:"" }));
    if (v !== "all") setSelectedMonth(Number(v));
  };

  const filterControls = (
    <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
      <FilterDropdown label="Year"  value={filter.year}  options={yearOptions}  onChange={handleYearChange}  width={120} />
      <FilterDropdown label="Month" value={filter.month} options={monthOptions} onChange={handleMonthChange} width={138} />
      {/* <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, fontWeight:600, color:C.textSoft }}>FROM</span>
        <input type="date" value={filter.fromDate}
          onChange={e=>setFilter(f=>({...f,fromDate:e.target.value,year:"all",month:"all"}))}
          style={{
            padding:"6px 8px", borderRadius:9, fontSize:11.5, fontWeight:500, color:C.text,
            border:`1.5px solid ${filter.fromDate ? C.teal : C.border}`,
            background: filter.fromDate ? `rgba(15,118,110,0.05)` : C.white,
            outline:"none", cursor:"pointer", fontFamily:"inherit",
          }} />
        <span style={{ fontSize:10, fontWeight:600, color:C.textSoft }}>TO</span>
        <input type="date" value={filter.toDate}
          onChange={e=>setFilter(f=>({...f,toDate:e.target.value,year:"all",month:"all"}))}
          style={{
            padding:"6px 8px", borderRadius:9, fontSize:11.5, fontWeight:500, color:C.text,
            border:`1.5px solid ${filter.toDate ? C.teal : C.border}`,
            background: filter.toDate ? `rgba(15,118,110,0.05)` : C.white,
            outline:"none", cursor:"pointer", fontFamily:"inherit",
          }} />
      </div> */}
    </div>
  );

  return (
    <>
      <div style={{
        background: C.glass,
        backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${C.glassBorder}`,
        padding: isMobile ? "8px 14px" : "9px 24px",
        position: "sticky",
        top:0,
        zIndex:20,
        boxShadow: "0 2px 12px rgba(15,118,110,0.06)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <SlidersHorizontal size={13} color={C.teal} />
            <span style={{ fontSize:11, fontWeight:700, color:C.textMid, letterSpacing:"0.04em", textTransform:"uppercase" }}>Filters</span>
            {activeCount > 0 && (
              <span style={{ background:C.teal, color:C.white, fontSize:9, fontWeight:800,
                borderRadius:20, padding:"1px 6px" }}>{activeCount}</span>
            )}
          </div>

          {isMobile ? (
            <button onClick={() => setMobileOpen(v=>!v)} style={{
              marginLeft:4, padding:"5px 10px", borderRadius:8, border:`1.5px solid ${C.border}`,
              background:C.white, cursor:"pointer", fontSize:11, fontWeight:600, color:C.textMid,
              display:"flex", alignItems:"center", gap:4,
            }}>
              {mobileOpen ? <X size={12}/> : <ChevronDown size={12}/>}
              {mobileOpen ? "Hide" : "Show"}
            </button>
          ) : (
            <>
              <div style={{ width:1, height:24, background:C.border }} />
              {filterControls}
            </>
          )}

          <div style={{ flex:1 }} />

          {activeCount > 0 && (
            <button onClick={onReset} style={{
              display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderRadius:9,
              border:`1.5px solid ${C.border}`, background:C.white, cursor:"pointer",
              fontSize:11.5, fontWeight:600, color:C.textMid, whiteSpace:"nowrap",
            }}>
              <RefreshCcw size={11} />
              Reset
            </button>
          )}

          <button onClick={() => setCalOpen(v => !v)} style={{
            width:34, height:34, borderRadius:9, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer", flexShrink:0,
            border:`1.5px solid ${calOpen ? C.teal : C.border}`,
            background: calOpen ? `rgba(15,118,110,0.08)` : C.white,
            color: calOpen ? C.teal : C.textMid,
          }}>
            <Calendar size={15} />
          </button>
        </div>

        {isMobile && mobileOpen && (
          <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
            {filterControls}
          </div>
        )}
      </div>

      {calOpen && (
        <CalendarPopup filter={filter} onSelectDate={handleCalDate} onClose={() => setCalOpen(false)} realDayData={realDayData} realYear={selectedYear} realMonth={selectedMonth} />
      )}
    </>
  );
}

function KpiGrid({ children, cols }) {
  const bp = useBreakpoint();
  const gridCols = bp === "xs" ? 1 : bp === "sm" ? 2 : bp === "md" ? cols > 3 ? 2 : cols : cols;
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${gridCols},1fr)`, gap:10 }}>
      {children}
    </div>
  );
}

function TwoCol({ left, right, ratio="2fr 1fr" }) {
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";
  return (
    <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":ratio, gap:12 }}>
      {left}
      {right}
    </div>
  );
}

function ThreeCol({ children }) {
  const bp = useBreakpoint();
  const cols = bp === "xs" ? 1 : bp === "sm" ? 1 : bp === "md" ? 2 : 3;
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${cols},1fr)`, gap:12 }}>
      {children}
    </div>
  );
}

function PLTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const colorMap = { profit:C.green, exp:C.red, rev:C.teal };
  return (
    <div style={{ background:"rgba(255,255,255,0.97)", backdropFilter:"blur(16px)",
      border:`1px solid ${C.border}`, borderRadius:12,
      padding:"10px 14px", boxShadow:"0 8px 32px rgba(0,0,0,0.14)", fontSize:12, minWidth:150 }}>
      {label && <div style={{ fontWeight:700, color:C.textSoft, fontSize:10.5,
        textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>{label}</div>}
      {payload.map((p,i) => (
        <div key={p.dataKey} style={{ display:"flex", alignItems:"center", gap:8,
          marginBottom: i < payload.length - 1 ? 5 : 0 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:colorMap[p.dataKey]||C.teal, flexShrink:0 }} />
          <span style={{ color:C.textMid, fontWeight:500, flex:1 }}>{p.name}</span>
          <span style={{ fontWeight:800, color:colorMap[p.dataKey]||C.text }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Tab Pages — identical UI, data now comes from context ────────────────────
function Overview({ data }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Total Revenue"   value={fmt(data.totalRevenue)}   icon={IndianRupee} />
        <KpiCard label="Total Orders"    value={data.totalOrders.toString()} icon={ShoppingCart} />
        <KpiCard label="Total Expenses"  value={fmt(data.totalExpenses)}  icon={TrendingDown} tint="red" />
        <KpiCard label="Net Profit"      value={fmt(data.totalProfit)}    icon={TrendingUp}
          tint={data.totalProfit >= 0 ? "green" : "red"}
          sub={`Margin: ${data.profitMargin}%`} />
      </KpiGrid>

      <TwoCol
        ratio="3fr 2fr"
        left={
          <Card title="Monthly Revenue Trend" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={210}>
              <ComposedChart data={data.monthly}>
                {GRADIENT_DEFS}
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="m" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip formatter={(v,n)=>n==="Revenue"?fmt(v):v} />} />
                <Bar dataKey="rev" fill="url(#gTeal)" radius={[6,6,0,0]} name="Revenue" />
                <Line dataKey="orders" stroke={C.accent} strokeWidth={2.5} dot={false} name="Orders" />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Expenses Breakdown" icon={TrendingDown}>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={data.expenseBreakdown} layout="vertical">
                {GRADIENT_DEFS}
                <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                <XAxis type="number" tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={100} tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
                <Bar dataKey="exp" radius={[0,6,6,0]} name="Expense">
                  {data.expenseBreakdown.map((_, i) => (
                    <Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
      />

      <TwoCol
        ratio="3fr 2fr"
        left={
          <Card title="Profit & Loss — Monthly" icon={BarChart3}>
            <ResponsiveContainer width="100%" height={210}>
              <ComposedChart data={data.plMonthly}>
                {GRADIENT_DEFS}
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="m" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
                <Tooltip content={<PLTooltip />} />
                <Legend wrapperStyle={{ fontSize:10.5, paddingTop:8 }} />
                <Bar dataKey="rev" fill="url(#gTeal)"  radius={[6,6,0,0]} name="Revenue" />
                <Bar dataKey="exp" fill="url(#gRed)"   radius={[6,6,0,0]} name="Expenses" />
                <Line dataKey="profit" stroke={C.green} strokeWidth={2.5}
                  dot={{ r:4, fill:C.green, strokeWidth:2, stroke:"#fff" }} name="Net Profit" />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Service Revenue" icon={Package}>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={data.serviceRevenue}>
                {GRADIENT_DEFS}
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="name" tick={AXIS_STYLE} angle={-10} textAnchor="end" height={40} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
                <Bar dataKey="rev" radius={[6,6,0,0]} name="Revenue">
                  {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
      />
    </div>
  );
}

function Orders({ data }) {
  const orderVolume = [
    {name:"Banners",count:Math.round(38*data.factor)},{name:"Booklets",count:Math.round(36*data.factor)},{name:"Brochures",count:Math.round(43*data.factor)},
    {name:"Bus.Cards",count:Math.round(31*data.factor)},{name:"Calendars",count:Math.round(49*data.factor)},{name:"Flyers",count:Math.round(46*data.factor)},
    {name:"Posters",count:Math.round(46*data.factor)},{name:"Stickers",count:Math.round(31*data.factor)},{name:"T-Shirts",count:Math.round(31*data.factor)},
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Completed" value={data.completedOrders.toString()} icon={CheckCircle} />
        <KpiCard label="Open Orders" value={data.openOrders.toString()} icon={Clock} />
        <KpiCard label="Rush Orders" value={data.rushOrders.toString()} icon={Zap} tint="accent" />
      <KpiCard label="Largest Order" value={fmt(data.biggestOrder)} icon={Star} tint="accent" />
      </KpiGrid>
      <TwoCol ratio="1fr 2fr"
        left={
          <Card title="Revenue Ranking" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.orderTypes} layout="vertical">
                {GRADIENT_DEFS}
                <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                <XAxis type="number" tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={82} tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
                <Bar dataKey="rev" fill="url(#gTeal)" radius={[0,6,6,0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Order Volume by Product" icon={Package}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={orderVolume}>
                {GRADIENT_DEFS}
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="name" tick={AXIS_STYLE} angle={-30} textAnchor="end" height={52} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill="url(#gBlue)" radius={[6,6,0,0]} name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
      />
      <Card title="Revenue vs Orders — Monthly Trend" icon={BarChart3}>
        <ResponsiveContainer width="100%" height={210}>
          <ComposedChart data={data.monthly}>
            {GRADIENT_DEFS}
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="m" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
            <YAxis yAxisId="rev" tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
            <YAxis yAxisId="cnt" orientation="right" tick={AXIS_STYLE} width={32} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip formatter={(v,n)=>n==="Revenue"?fmt(v):v} />} />
            <Bar yAxisId="rev" dataKey="rev" fill="url(#gTeal)" radius={[6,6,0,0]} name="Revenue" />
            <Line yAxisId="cnt" dataKey="orders" stroke={C.accent} strokeWidth={2.5}
              dot={{ r:4, fill:C.accent, strokeWidth:2, stroke:"#fff" }} name="Orders" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function Customers({ data }) {
  const paymentData = [
    {name:"Cash",value:Math.round(149663*data.factor)},{name:"Credit Card",value:Math.round(118842*data.factor)},
    {name:"UPI",value:Math.round(114139*data.factor)},{name:"Bank Transfer",value:Math.round(63259*data.factor)},
    {name:"Debit Card",value:Math.round(99650*data.factor)},{name:"Cheque",value:Math.round(19274*data.factor)},
  ];
  const monthlySegment = data.monthly.slice(0,6).map(r=>({
    m:r.m,
    corp:Math.round(r.rev*0.302),smb:Math.round(r.rev*0.367),
    ind:Math.round(r.rev*0.119),govt:Math.round(r.rev*0.099),
  }));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Corporate Revenue" value={fmt(data.segmentRevData[0]?.rev||0)} icon={IndianRupee} />
        <KpiCard label="Small Biz Revenue" value={fmt(data.segmentRevData[4]?.rev||0)} icon={IndianRupee} />
        <KpiCard label="Individual Orders" value={Math.round(data.totalOrders*0.288).toString()} icon={Users} />
        <KpiCard label="Repeat Customers" value={Math.round(data.totalOrders*0.192).toString()} icon={Star} tint="accent" />
      </KpiGrid>
      <ThreeCol>
        <Card title="Segment Revenue" icon={Users}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.segmentRevData}>
              {GRADIENT_DEFS}
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="name" tick={AXIS_STYLE} angle={-15} textAnchor="end" height={44} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
              <Bar dataKey="rev" radius={[6,6,0,0]} name="Revenue">
                {data.segmentRevData.map((_,i)=><Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Customer Acquisition" icon={Star}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={[
                {name:"Phone",value:17.1},{name:"Referral",value:16.2},
                {name:"Repeat",value:19.6},{name:"Walk-in",value:30.3},{name:"Website",value:16.7},
              ]} cx="50%" cy="44%" innerRadius={52} outerRadius={80} dataKey="value" paddingAngle={3}
                strokeWidth={0}>
                {[C.teal,C.tealLight,C.accent,C.purple,"#3B82F6"].map((c,i)=><Cell key={i} fill={c} />)}
              </Pie>
              <Tooltip formatter={v=>`${v}%`} />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Payment Methods" icon={CreditCard}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={paymentData} layout="vertical">
              {GRADIENT_DEFS}
              <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
              <XAxis type="number" tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={80} tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
              <Bar dataKey="value" radius={[0,6,6,0]} name="Amount">
                {paymentData.map((_,i)=><Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </ThreeCol>
      <Card title="Monthly Segment Trend" icon={BarChart3}>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={monthlySegment}>
            {GRADIENT_DEFS}
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="m" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
            <Legend wrapperStyle={{ fontSize:10.5, paddingTop:8 }} />
            <Bar dataKey="corp" stackId="a" fill={C.teal}      name="Corporate" />
            <Bar dataKey="smb"  stackId="a" fill={C.tealLight} name="Small Biz" />
            <Bar dataKey="ind"  stackId="a" fill={C.accent}    name="Individual" />
            <Bar dataKey="govt" stackId="a" fill={C.purple}    name="Government" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function Services({ data }) {
  const statusData = [
    {service:"Digital",   pending:Math.round(30*data.factor), inProgress:Math.round(45*data.factor), completed:Math.round(100*data.factor), cancelled:Math.round(10*data.factor)},
    {service:"Clothing",  pending:Math.round(42*data.factor), inProgress:Math.round(58*data.factor), completed:Math.round(130*data.factor), cancelled:Math.round(14*data.factor)},
    {service:"Mugs",      pending:Math.round(25*data.factor), inProgress:Math.round(38*data.factor), completed:Math.round(75*data.factor),  cancelled:Math.round(8*data.factor)},
    {service:"Resin",     pending:Math.round(18*data.factor), inProgress:Math.round(22*data.factor), completed:Math.round(52*data.factor),  cancelled:Math.round(6*data.factor)},
  ];
  const totalUnits = data.serviceRevenue.reduce((s,r)=>s+(r.units||0),0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Digital Print" value={fmt(data.serviceRevenue[0]?.rev||0)} icon={Printer} />
        <KpiCard label="Clothing"      value={fmt(data.serviceRevenue[1]?.rev||0)} icon={Shirt} tint="purple" />
        <KpiCard label="Mugs"          value={fmt(data.serviceRevenue[2]?.rev||0)} icon={Coffee} tint="accent" />
        <KpiCard label="Resin"         value={fmt(data.serviceRevenue[3]?.rev||0)} icon={Layers} />
      </KpiGrid>
      <ThreeCol>
        <Card title="Service Revenue" icon={Package}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.serviceRevenue}>
              {GRADIENT_DEFS}
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="name" tick={AXIS_STYLE} angle={-12} textAnchor="end" height={44} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} width={46} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatter={v=>fmt(v)} />} />
              <Bar dataKey="rev" radius={[6,6,0,0]} name="Revenue">
                {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Status Distribution" icon={BarChart3}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusData}>
              {GRADIENT_DEFS}
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="service" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize:10, paddingTop:8 }} />
              <Bar dataKey="pending"    stackId="a" fill={C.accent}    name="Pending" />
              <Bar dataKey="inProgress" stackId="a" fill={C.tealLight} name="In Progress" />
              <Bar dataKey="completed"  stackId="a" fill={C.teal}      name="Completed" />
              <Bar dataKey="cancelled"  stackId="a" fill={C.red}       name="Cancelled" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Production Volume" icon={Package}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.serviceRevenue} layout="vertical">
              {GRADIENT_DEFS}
              <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
              <XAxis type="number" tick={AXIS_STYLE} tickFormatter={v=>`${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={80} tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatter={v=>`${(v||0).toLocaleString()} units`} />} />
              <Bar dataKey="units" radius={[0,6,6,0]} name="Units">
                {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BAR_GRADIENTS[i % BAR_GRADIENTS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </ThreeCol>
      <KpiGrid cols={4}>
        <KpiCard label="Total Units Produced" value={totalUnits.toLocaleString()} icon={Hash} tint="accent" />
        <KpiCard label="Avg Rev / Service"     value={fmt(Math.round(data.totalRevenue / Math.max(data.serviceRevenue.length, 1)))} icon={BarChart3} />
        <KpiCard label="Completed Jobs"        value={data.completedOrders.toString()} icon={CheckCircle} />
        <KpiCard label="Rush Orders"           value={data.rushOrders.toString()} icon={Zap} tint="purple" />
      </KpiGrid>
    </div>
  );
}

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  delivered:          { label:"Delivered",          bg:"#DCFCE7", color:"#15803D", dot:"#22C55E" },
  completed:          { label:"Completed",           bg:"#DCFCE7", color:"#15803D", dot:"#22C55E" },
  placed:             { label:"Placed",              bg:"#E0F2FE", color:"#0369A1", dot:"#38BDF8" },
  pending:            { label:"Pending",             bg:"#FEF9C3", color:"#854D0E", dot:"#EAB308" },
  inspection_pending: { label:"Inspection",          bg:"#FEF9C3", color:"#854D0E", dot:"#EAB308" },
  "in progress":      { label:"In Progress",         bg:"#FEF3C7", color:"#92400E", dot:"#F59E0B" },
  cancelled:          { label:"Cancelled",           bg:"#FEE2E2", color:"#991B1B", dot:"#EF4444" },
  return_requested:   { label:"Return Requested",    bg:"#FEE2E2", color:"#991B1B", dot:"#EF4444" },
  refunded:           { label:"Refunded",            bg:"#F3E8FF", color:"#6B21A8", dot:"#A855F7" },
  refund_processing:  { label:"Refund Processing",   bg:"#FDF4FF", color:"#7E22CE", dot:"#C084FC" },
};
const getStatusCfg = (s) => STATUS_CONFIG[(s||"").toLowerCase()] ?? { label:s||"—", bg:"#F3F4F6", color:"#6B7280", dot:"#9CA3AF" };

function StatusBadge({ status }) {
  const cfg = getStatusCfg(status);
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
      background:cfg.bg, color:cfg.color, borderRadius:20,
      padding:"3px 10px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:cfg.dot }} />
      {cfg.label}
    </span>
  );
}

// ─── OrderSearch ──────────────────────────────────────────────────────────────
function OrderSearch() {
  const { derived } = useDashboard();
  const rawOrders = derived?.rawOrders ?? [];
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";

  // Deduplicate
  const orderMap = {};
  rawOrders.forEach(o => { if (!orderMap[o.order_id]) orderMap[o.order_id] = o; });
  const allOrders = Object.values(orderMap);

  const [query,     setQuery]     = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected,  setSelected]  = useState(null);
  const searchRef = useRef(null);

  // Auto-select first on load
  useEffect(() => {
    if (allOrders.length > 0 && !selected) setSelected(allOrders[0]);
  }, [allOrders.length]); // eslint-disable-line

  // Unique statuses for filter pills
  const statuses = ["all", ...Array.from(new Set(allOrders.map(o => (o.status||"").toLowerCase()).filter(Boolean)))];

  // Filtered list
  const q = query.toLowerCase().trim();
  const filtered = allOrders.filter(o => {
    const matchQ = !q ||
      (o.order_id||"").toLowerCase().includes(q) ||
      (o.customer_name||"").toLowerCase().includes(q) ||
      (o.product_name||"").toLowerCase().includes(q) ||
      (o.category||"").toLowerCase().includes(q);
    const matchS = statusFilter === "all" || (o.status||"").toLowerCase() === statusFilter;
    return matchQ && matchS;
  });

  // Totals for filtered list
  const totalAmt   = filtered.reduce((s, o) => s + parseFloat(o.amount || 0), 0);
  const totalQty   = filtered.reduce((s, o) => s + parseInt(o.quantity || 0, 10), 0);
  const order      = selected && orderMap[selected.order_id] ? selected : filtered[0] || null;

  if (allOrders.length === 0) {
    return (
      <div style={{ padding:60, textAlign:"center" }}>
        <FileText size={40} color={C.border} style={{ margin:"0 auto 12px" }} />
        <div style={{ fontSize:14, color:C.textSoft, fontWeight:500 }}>No orders found for this period.</div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:isMobile ? "column" : "row", gap:16, alignItems:"flex-start" }}>

      {/* ── LEFT PANEL: search + list ── */}
      <div style={{ width:isMobile?"100%":340, flexShrink:0, display:"flex", flexDirection:"column", gap:10 }}>

        {/* Search input */}
        <div style={{ position:"relative" }}>
          <Search size={15} color={C.textSoft} style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }} />
          <input
            ref={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by ID, customer, product…"
            style={{
              width:"100%", padding:"10px 36px 10px 38px",
              borderRadius:12, border:`1.5px solid ${query ? C.teal : C.border}`,
              background:C.white, fontSize:13, color:C.text, outline:"none",
              transition:"border-color .15s", boxSizing:"border-box",
            }}
            onFocus={e => { e.target.style.borderColor = C.teal; e.target.style.boxShadow = "0 0 0 3px rgba(15,118,110,0.10)"; }}
            onBlur={e  => { e.target.style.borderColor = query ? C.teal : C.border; e.target.style.boxShadow = "none"; }}
          />
          {query && (
            <button onClick={() => setQuery("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
              background:"none", border:"none", cursor:"pointer", padding:2, color:C.textSoft }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {statuses.map(s => {
            const cfg = s === "all" ? { bg:"#F3F4F6", color:C.textMid, dot:C.textSoft } : getStatusCfg(s);
            const isAct = statusFilter === s;
            return (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding:"4px 10px", borderRadius:20, border:`1.5px solid ${isAct ? cfg.dot : C.border}`,
                background: isAct ? cfg.bg : C.white, color: isAct ? cfg.color : C.textSoft,
                fontSize:11, fontWeight:isAct ? 700 : 500, cursor:"pointer", transition:"all .15s",
                textTransform:"capitalize",
              }}>
                {s === "all" ? `All (${allOrders.length})` : cfg.label}
              </button>
            );
          })}
        </div>

        {/* Summary strip */}
        {filtered.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
            {[
              ["Results",  filtered.length, C.teal],
              ["Revenue",  fmt(totalAmt),   C.green],
              ["Qty",      totalQty,        C.accent],
            ].map(([label, val, clr]) => (
              <div key={label} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
                padding:"8px 10px", textAlign:"center" }}>
                <div style={{ fontSize:14, fontWeight:800, color:clr }}>{val}</div>
                <div style={{ fontSize:9.5, color:C.textSoft, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Order list */}
        <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
          overflow:"hidden", maxHeight:isMobile ? 280 : 460, overflowY:"auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding:24, textAlign:"center", color:C.textSoft, fontSize:12 }}>No orders match your search.</div>
          ) : filtered.map((o, i) => {
            const cfg = getStatusCfg(o.status);
            const isAct = order?.order_id === o.order_id;
            return (
              <div key={o.order_id} onClick={() => setSelected(o)} style={{
                padding:"11px 14px", cursor:"pointer", borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none",
                background: isAct ? "rgba(15,118,110,0.06)" : i % 2 === 0 ? C.white : "#FAFAFA",
                borderLeft: isAct ? `3px solid ${C.teal}` : "3px solid transparent",
                transition:"background .12s",
              }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12.5, fontWeight:700, color: isAct ? C.teal : C.text }}>
                    {o.order_id}
                  </span>
                  <StatusBadge status={o.status} />
                </div>
                <div style={{ fontSize:11.5, color:C.textMid, marginBottom:2 }}>{o.customer_name}</div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span style={{ fontSize:11, color:C.textSoft, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"60%" }}>
                    {o.product_name || o.category || "—"}
                  </span>
                  <span style={{ fontSize:12, fontWeight:700, color:C.teal }}>{fmt(parseFloat(o.amount||0))}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT PANEL: order detail ── */}
      {order ? (
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:12 }}>

          {/* Header */}
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16,
            padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between",
            flexWrap:"wrap", gap:10, boxShadow:C.shadow }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <FileText size={16} color={C.teal} />
                <span style={{ fontSize:16, fontWeight:800, color:C.text, letterSpacing:"-0.02em" }}>
                  {order.order_id}
                </span>
              </div>
              <div style={{ fontSize:11.5, color:C.textSoft }}>
                {order.date ? new Date(order.date).toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" }) : "—"}
              </div>
            </div>
            <StatusBadge status={order.status} />
          </div>

          {/* Detail sections */}
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>

            {/* Customer Info */}
            <DetailSection title="Customer Information" icon={Users} color="#E0F2FE" iconColor="#0369A1">
              <DetailRow label="Name"    value={order.customer_name} />
              <DetailRow label="Phone"   value={order.phone || order.customer_phone || "—"} />
              <DetailRow label="Email"   value={order.email || order.customer_email || "—"} />
              <DetailRow label="Address" value={order.delivery_address || order.address || "—"} />
            </DetailSection>

            {/* Product Info */}
            <DetailSection title="Product Information" icon={Package} color="#DCFCE7" iconColor="#15803D">
              <DetailRow label="Product"   value={order.product_name} />
              <DetailRow label="Category"  value={order.category} />
              <DetailRow label="Quantity"  value={String(order.quantity || 0)} />
              <DetailRow label="Unit"      value={order.unit || "pcs"} />
            </DetailSection>

            {/* Financial Info */}
            <DetailSection title="Financial Details" icon={DollarSign} color="#FEF9C3" iconColor="#854D0E">
              <DetailRow label="Total Amount"    value={fmt(parseFloat(order.amount || 0))} highlight />
              <DetailRow label="Payment Method"  value={order.payment_method || "—"} />
              <DetailRow label="Discount"        value={order.discount ? fmt(parseFloat(order.discount)) : "₹0"} />
              <DetailRow label="Tax"             value={order.tax ? fmt(parseFloat(order.tax)) : "—"} />
            </DetailSection>

            {/* Order Status */}
            <DetailSection title="Order Status & Tracking" icon={Truck} color="#F3E8FF" iconColor="#6B21A8">
              <DetailRow label="Status"          value={<StatusBadge status={order.status} />} />
              <DetailRow label="Order Date"      value={order.date?.slice(0,10) || "—"} />
              <DetailRow label="Delivery Date"   value={order.delivery_date || order.expected_date || "—"} />
              <DetailRow label="Tracking No."    value={order.tracking_number || "—"} />
            </DetailSection>
          </div>

          {/* Notes / extra */}
          {(order.notes || order.remarks || order.description) && (
            <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
              padding:"14px 16px", boxShadow:C.shadow }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textSoft, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:8 }}>Notes / Remarks</div>
              <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>
                {order.notes || order.remarks || order.description}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center",
          padding:48, color:C.textSoft, fontSize:13 }}>
          <div style={{ textAlign:"center" }}>
            <Search size={36} color={C.border} style={{ margin:"0 auto 12px" }} />
            <div style={{ fontWeight:500 }}>Select an order to view details</div>
          </div>
        </div>
      )}
    </div>
  );
}

// Detail section + row helpers
function DetailSection({ title, icon: Icon, color, iconColor, children }) {
  return (
    <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
      overflow:"hidden", boxShadow:C.shadow }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 16px",
        borderBottom:`1px solid ${C.border}`, background:color+"40" }}>
        <div style={{ width:26, height:26, borderRadius:8, background:color,
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Icon size={13} color={iconColor} />
        </div>
        <span style={{ fontSize:11.5, fontWeight:700, color:iconColor, letterSpacing:"-0.01em" }}>{title}</span>
      </div>
      <div style={{ padding:"4px 0" }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"130px 1fr", borderBottom:`1px solid #F3F4F6` }}>
      <div style={{ padding:"9px 16px", fontSize:11, fontWeight:600, color:C.textSoft,
        borderRight:`1px solid #F3F4F6` }}>{label}</div>
      <div style={{ padding:"9px 16px", fontSize:highlight ? 14 : 12.5,
        fontWeight: highlight ? 800 : 500, color: highlight ? C.teal : C.text,
        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

const TABS = [
  { id:"overview",  label:"Overview",  icon:Home },
  { id:"orders",    label:"Orders",    icon:ShoppingCart },
  { id:"customers", label:"Customers", icon:Users },
  { id:"services",  label:"Services",  icon:Printer },
  { id:"search",    label:"Search",    icon:Search },
];

function TopNav({ active, setActive }) {
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";
  const [menuOpen, setMenuOpen] = useState(false);

  if (isMobile) {
    return (
      <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
        position:"sticky", top:0, zIndex:39, boxShadow:"0 1px 8px rgba(15,118,110,0.07)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 14px", height:48 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:12, fontWeight:600, color:C.textMid }}>
              {TABS.find(t=>t.id===active)?.label}
            </span>
            <button onClick={()=>setMenuOpen(v=>!v)} style={{
              width:36, height:36, borderRadius:9, border:`1.5px solid ${C.border}`,
              background:C.white, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer" }}>
              {menuOpen ? <X size={16} color={C.teal} /> : <Menu size={16} color={C.textMid} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div style={{ borderTop:`1px solid ${C.border}`, background:C.white }}>
            {TABS.map(({ id, label, icon:Icon }) => (
              <button key={id} onClick={()=>{setActive(id);setMenuOpen(false);}} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10, padding:"12px 16px",
                border:"none", background: active===id ? `rgba(15,118,110,0.07)` : "transparent",
                cursor:"pointer", fontSize:13.5, fontWeight: active===id ? 700 : 500,
                color: active===id ? C.teal : C.text,
                borderLeft: active===id ? `3px solid ${C.teal}` : "3px solid transparent",
              }}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        )}
      </header>
    );
  }

  return (
    <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
      position:"sticky", top:0, zIndex:0, boxShadow:"0 1px 8px rgba(0,0,0,0.04)" }}>
      <div style={{ display:"flex", alignItems:"center", padding:"0 20px", height:50, gap:4 }}>
        {TABS.map(({ id, label, icon:Icon }) => {
          const isAct = active === id;
          return (
            <button key={id} onClick={()=>setActive(id)} style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"6px 14px", borderRadius:10, border:"none",
              cursor:"pointer",
              background: isAct ? `rgba(15,118,110,0.09)` : "transparent",
              color: isAct ? C.teal : C.textSoft,
              fontWeight: isAct ? 700 : 500, fontSize:12.5,
              transition:"all .15s", whiteSpace:"nowrap",
            }}>
              <Icon size={14} color={isAct ? C.teal : C.textSoft} />
              {label}
            </button>
          );
        })}
      </div>
    </header>
  );
}

function ActiveFilterBanner({ filter }) {
  const parts = [];
  if (String(filter.year) !== "all") parts.push(`Year: ${filter.year}`);
  if (String(filter.month) !== "all") parts.push(`Month: ${MONTHS[Number(filter.month)-1]}`);
  if (filter.fromDate) parts.push(`From: ${filter.fromDate}`);
  if (filter.toDate) parts.push(`To: ${filter.toDate}`);
  if (parts.length === 0) return null;
  return (
    <div style={{
      background:`linear-gradient(90deg, rgba(15,118,110,0.08) 0%, rgba(20,184,166,0.05) 100%)`,
      borderBottom:`1px solid rgba(15,118,110,0.12)`,
      padding:"6px 24px",
      display:"flex", alignItems:"center", gap:7, flexWrap:"wrap",
    }}>
      <Filter size={10} color={C.teal} />
      <span style={{ fontSize:10.5, fontWeight:700, color:C.teal, letterSpacing:"0.04em" }}>ACTIVE:</span>
      {parts.map(p => (
        <span key={p} style={{
          background:C.white, border:`1px solid rgba(15,118,110,0.2)`, borderRadius:20,
          padding:"2px 9px", fontSize:10.5, fontWeight:600, color:C.tealDark,
        }}>{p}</span>
      ))}
    </div>
  );
}

// ─── Root App — reads data from context instead of deriveData() ───────────────
export default function App() {
  const [tab, setTab] = useState("overview");
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const bp = useBreakpoint();

  // ← only change: pull derived data from context
  const { derived, dashboardLoading } = useDashboard();

  // While loading on first mount, show a lightweight skeleton using zeros
  const EMPTY = {
    monthly:[], totalRevenue:0, totalOrders:0, totalExpenses:0,
    avgOrder:0, biggestOrder:0, completedOrders:0, openOrders:0, rushOrders:0,
    orderTypes:[], serviceRevenue:[], segmentRevData:[], factor:0,
    expenseBreakdown:[], plMonthly:[], totalProfit:0, profitMargin:0, rawOrders:[],
  };
  const data = derived ?? EMPTY;

  const activeCount = [
    String(filter.year)  !== String(DEFAULT_FILTER.year),
    String(filter.month) !== String(DEFAULT_FILTER.month),
    filter.fromDate !== DEFAULT_FILTER.fromDate,
    filter.toDate   !== DEFAULT_FILTER.toDate,
  ].filter(Boolean).length;

  const handleReset = () => setFilter(DEFAULT_FILTER);

  const pageMap = {
    overview:  <Overview  data={data} />,
    orders:    <Orders    data={data} />,
    customers: <Customers data={data} />,
    services:  <Services  data={data} />,
    search:    <OrderSearch />,
  };

  const padding = bp === "xs" ? "16px 12px" : bp === "sm" ? "16px 14px" : "24px 28px";

  return (
    <>
      <style>{`
        @keyframes dropIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn  { from { opacity:0; transform:scale(0.94) translateY(-10px); } to { opacity:1; transform:scale(1) translateY(0); } }
        * { box-sizing: border-box; margin:0; padding:0; }
        input[type="date"] { font-family: inherit; }
        input[type="date"]:focus { border-color: #0F766E !important; box-shadow: 0 0 0 3px rgba(15,118,110,0.12); outline: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
      `}</style>
      <div style={{ minHeight:"100vh", background:"#F9FAFB", fontFamily:"'Inter','DM Sans','Segoe UI',sans-serif",
        opacity: dashboardLoading ? 0.6 : 1, transition:"opacity .2s" }}>
        <TopNav active={tab} setActive={setTab} />
        <FilterBar filter={filter} setFilter={setFilter} activeCount={activeCount} onReset={handleReset} />
        <ActiveFilterBanner filter={filter} />
        <main style={{ padding, maxWidth:1400, margin:"0 auto" }}>
          {pageMap[tab]}
        </main>
      </div>
    </>
  );
}